/**
 * @typedef {object} RecentlyViewedDetails
 * @property {string} url
 * @property {string} title
 * @property {string} [image]
 * @property {string} [price]
 */

/**
 * Updates the recently viewed products in localStorage.
 */
export class RecentlyViewed {
  /** @static @constant {string} The key used to store the viewed products in session storage */
  static #STORAGE_KEY = 'viewedProducts';
  /** @static @constant {string} Richer product cards for the mobile drawer */
  static #DETAILS_KEY = 'viewedProductDetails';
  /** @static @constant {number} The maximum number of products to store */
  static #MAX_PRODUCTS = 4;

  /**
   * Adds a product to the recently viewed products list.
   * @param {string} productId - The ID of the product to add.
   * @param {RecentlyViewedDetails} [details] - Optional card data for the menu drawer.
   */
  static addProduct(productId, details) {
    let viewedProducts = this.getProducts();

    viewedProducts = viewedProducts.filter((/** @type {string} */ id) => String(id) !== String(productId));
    viewedProducts.unshift(productId);
    viewedProducts = viewedProducts.slice(0, this.#MAX_PRODUCTS);

    localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(viewedProducts));

    if (!details) return;

    const map = this.getDetails();
    map[String(productId)] = details;
    for (const key of Object.keys(map)) {
      if (!viewedProducts.some((id) => String(id) === key)) {
        delete map[key];
      }
    }
    localStorage.setItem(this.#DETAILS_KEY, JSON.stringify(map));
  }

  static clearProducts() {
    localStorage.removeItem(this.#STORAGE_KEY);
    localStorage.removeItem(this.#DETAILS_KEY);
  }

  /**
   * Retrieves the list of recently viewed products from session storage.
   * @returns {string[]} The list of viewed products.
   */
  static getProducts() {
    return JSON.parse(localStorage.getItem(this.#STORAGE_KEY) || '[]');
  }

  /**
   * Card details keyed by product id, used by the mobile drawer grid.
   * @returns {Record<string, RecentlyViewedDetails>}
   */
  static getDetails() {
    try {
      return JSON.parse(localStorage.getItem(this.#DETAILS_KEY) || '{}');
    } catch {
      return {};
    }
  }
}
